import { type FormEvent, useEffect, useState } from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  BoardCard,
  BoardMember,
  CardInput,
  CardPriority,
} from "@/features/board/types";

type CardDialogProps = {
  card: BoardCard | null;
  columnId: string;
  members: BoardMember[];
  open: boolean;
  onClose: () => void;
  onDelete?: (cardId: string) => Promise<void>;
  onSave: (input: CardInput) => Promise<void>;
};

const dateValue = (value?: string | null) =>
  value ? new Date(value).toISOString().slice(0, 10) : "";

export const CardDialog = ({
  card,
  columnId,
  members,
  open,
  onClose,
  onDelete,
  onSave,
}: CardDialogProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<CardPriority>("medium");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(card?.title ?? "");
    setDescription(card?.description ?? "");
    setPriority(card?.priority ?? "medium");
    setAssigneeId(card?.assigneeId ?? "");
    setDueDate(dateValue(card?.dueAt));
    setConfirmDelete(false);
    setError(null);
  }, [card, open]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave({
        columnId,
        title,
        description,
        priority,
        assigneeId: assigneeId || null,
        dueAt: dueDate ? new Date(`${dueDate}T12:00:00`).toISOString() : null,
      });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The card could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!card || !onDelete) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onDelete(card.id);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The card could not be deleted.");
      setSaving(false);
    }
  };

  return (
    <Dialog
      description={card ? "Update the work, owner, and timing." : "Capture the next concrete piece of work."}
      onClose={onClose}
      open={open}
      title={card ? "Edit card" : "New card"}
    >
      <form className="flex flex-col gap-4" onSubmit={submit}>
        {error ? <Alert className="border-0 bg-red-50 text-red-800 ring-1 ring-red-900/10">{error}</Alert> : null}
        <div className="flex flex-col gap-2">
          <Label htmlFor="card-title">Title</Label>
          <Input
            autoFocus
            id="card-title"
            maxLength={140}
            name="title"
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Describe the outcome"
            required
            value={title}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="card-description">Description</Label>
          <Textarea
            id="card-description"
            maxLength={4000}
            name="description"
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Add context, acceptance notes, or links."
            value={description}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="card-priority">Priority</Label>
            <Select
              id="card-priority"
              name="priority"
              onChange={(event) => setPriority(event.target.value as CardPriority)}
              value={priority}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="card-assignee">Assignee</Label>
            <Select
              id="card-assignee"
              name="assigneeId"
              onChange={(event) => setAssigneeId(event.target.value)}
              value={assigneeId}
            >
              <option value="">Unassigned</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  @{member.username}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="card-due-date">Due date</Label>
          <Input
            id="card-due-date"
            name="dueDate"
            onChange={(event) => setDueDate(event.target.value)}
            type="date"
            value={dueDate}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div>
            {card && onDelete ? (
              <Button
                className={confirmDelete ? "text-red-700" : undefined}
                disabled={saving}
                onClick={() => void remove()}
                type="button"
                variant="secondary"
              >
                {confirmDelete ? "Confirm delete" : "Delete card"}
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose} type="button" variant="ghost">
              Cancel
            </Button>
            <Button disabled={saving} type="submit">
              {saving ? "Saving" : "Save card"}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};
